import { Box, Grid, styled } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import {
  LwFormDate,
  LwFormInput,
  LwFormMultiSelect,
  LwFormNumberInput,
  StyledLabel,
} from 'redesign';
import { useGetFlexPoolsForCompany } from 'ogp/components/views/planning/flex-pools/flex-pool-create/hooks/use-get-flex-pools-for-company';
import { RenderUnsettledUI } from 'shared/components';
import { FormMode } from 'shared/components/shift-forms/components/forms/shared/types';
import { useTranslation } from 'react-i18next';
import { EmployeeContractStartDateInfoTooltip } from './components/employee-contract-start-date-info-tooltip';
import { EmployeeFormData } from '../employee-edit/employee-update';

export const EmployeeForm = ({ formState }: { formState: FormMode }) => {
  const { control } = useFormContext<EmployeeFormData>();
  const flexPoolsQuery = useGetFlexPoolsForCompany();
  const { t } = useTranslation('all-platforms-common');

  if (flexPoolsQuery.status !== 'success') {
    return <RenderUnsettledUI data={flexPoolsQuery} />;
  }

  return (
    <form aria-label="Employee form">
      <Grid container item xs={9} spacing={2}>
        <Grid item xs={6}>
          {formState === 'create' && (
            <LwFormInput
              name="email"
              control={control}
              label={t('worker_data.email')}
              rules={{ required: 'Voer een email in' }}
            />
          )}
          <LwFormInput
            name="firstName"
            control={control}
            label={t('worker_data.first_name')}
            rules={{ ...(formState === 'create' ? { required: 'Voer een voornaam in' } : {}) }}
            disabled={formState === 'update'}
          />
          <LwFormInput
            name="infix"
            control={control}
            label={t('worker_data.infix')}
            disabled={formState === 'update'}
          />
          <LwFormInput
            name="lastName"
            control={control}
            label={t('worker_data.last_name')}
            rules={{ ...(formState === 'create' ? { required: 'Voer een achternaam in' } : {}) }}
            disabled={formState === 'update'}
          />
          {formState === 'update' && (
            <LwFormMultiSelect
              name="employeeFlexPools"
              control={control}
              options={flexPoolsQuery.data || []}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.name}
              data-testid="employee-flexpools"
              label={t('flexpools.name')}
            />
          )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <LwFormDate
                name="contractStartDate"
                control={control}
                rules={{ required: t('worker-data.contract-start-date-required') }}
                label={
                  <ItemWrapper>
                    <StyledLabel>{t('worker-data.contract-start-date')}</StyledLabel>
                    <EmployeeContractStartDateInfoTooltip />
                  </ItemWrapper>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <LwFormDate
                name="contractEndDate"
                control={control}
                label={t('worker-data.contract-end-date')}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <LwFormNumberInput
                name="contractHours"
                control={control}
                step={1}
                min={0}
                label={t('worker-data.contract-hours')}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

const ItemWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  color: theme.palette.lwBlack[60],
}));
